import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { MeModel, authApi } from '../apis/authApi';
import { Permission } from '@casops/permissions';
import { getBaseHref } from '@casops/bootstrap';
import i18n from '@casops/i18n';
import { ErrorResponse } from '@dmc/ui';

export interface UserAuthData {
  permissions: Permission[];
  isLoggedIn: boolean;
  isRedirecting: boolean;
  actionLoggingIn?: boolean;
  displayName?: string;
  displayInitials?: string;
  language: string;
}

const fallbackLanguage = 'en';
const baseHref = getBaseHref();
const loginUrl = baseHref;
const noAccessUrl = baseHref.endsWith('/') ? `${baseHref}no-access` : `${baseHref}/no-access`;

const clearState = (state: UserAuthData) => {
  state.isLoggedIn = false;
  state.actionLoggingIn = true;
  state.permissions = [];
  state.displayName = undefined;
  state.displayInitials = undefined;
  state.language = fallbackLanguage;
};

const processMeResponse = (state: UserAuthData, payload: MeModel) => {
  state.isLoggedIn = true;
  state.isRedirecting = false;
  state.actionLoggingIn = false;
  state.permissions = payload.permissions;
  state.displayName = payload.displayName;
  state.displayInitials = payload.initials;
  state.language = i18n.language;
};

const initialState: UserAuthData = {
  permissions: [],
  isLoggedIn: false,
  isRedirecting: true,
  actionLoggingIn: true,
  displayName: undefined,
  displayInitials: undefined,
  language: fallbackLanguage,
};

const authSlice = createSlice({
  name: 'auth',
  initialState: { ...initialState },
  reducers: {
    setLanguage: (state, action) => {
      state.language = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(authApi.endpoints.login.matchFulfilled, (state, { payload }) => {
      processMeResponse(state, payload);
    });
    builder.addMatcher(authApi.endpoints.login.matchRejected, state => {
      clearState(state);
    });

    builder.addMatcher(authApi.endpoints.logout.matchFulfilled, state => {
      clearState(state);
      state.isRedirecting = true;
    });

    builder.addMatcher(authApi.endpoints.me.matchFulfilled, (state, { payload }) => {
      processMeResponse(state, payload);
    });
    builder.addMatcher(authApi.endpoints.me.matchRejected, (state, { payload }) => {
      clearState(state);

      if (ErrorResponse.isStatusError(payload)) {
        if (payload.status === 403) {
          console.debug('navigating to no access');
          state.isRedirecting = false;
          window.location.href = noAccessUrl;
        } else if (payload.status === 401) {
          console.debug('navigating to login');
          state.isRedirecting = true;
          window.location.href = loginUrl;
        }
      }
    });
  },
});

export default authSlice.reducer;

export const { setLanguage } = authSlice.actions;

export const selectCurrentUser = (state: RootState) => state.auth;
